import { SwapStatus, TransactionStatus, StepState, DepositStatus } from '../types/transactions'

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (err) {
    console.error('Failed to copy text: ', err)
    return false
  }
}

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString)

  if (isNaN(date.getTime())) {
    return '-'
  }

  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  const hours = String(date.getUTCHours()).padStart(2, '0')
  const minutes = String(date.getUTCMinutes()).padStart(2, '0')

  return `${year}-${month}-${day} ${hours}:${minutes} UTC`
}

export const getNetworkName = (chain: string, symbol?: string) => {
  switch (chain) {
    case 'BTC':
      return 'Bitcoin'
    case 'BCH':
      return 'Bitcoin Cash'
    case 'LTC':
      return 'Litecoin'
    case 'SOL':
      return 'Solana'
    case 'POL':
      return 'Polygon'
    case 'TRON':
      return 'Tron'
    case 'ETH':
      return 'ERC 20'
    case 'BSC':
      return 'BEP 20'
    case 'POLYGON':
      return 'Polygon'
    case 'SOLANA':
      return 'Solana'
    case 'FIAT':
      return 'Fiat'
    default:
      return symbol || chain
  }
}

export const getDepositStatusBadgeProps = (status: TransactionStatus) => {
  switch (status) {
    case TransactionStatus.Pending:
      return {
        variant: 'secondary' as const,
        className: 'bg-orange-100 text-orange-800',
        icon: 'Clock',
        text: 'Pending Deposit',
        status: 'PENDING',
      }
    case TransactionStatus.Approved:
      return {
        variant: 'secondary' as const,
        className: 'bg-green-100 text-green-800',
        icon: 'Check',
        text: 'Approved',
        status: 'APPROVED',
      }
    case TransactionStatus.Rejected:
      return {
        variant: 'secondary' as const,
        className: 'bg-red-100 text-red-800',
        icon: 'X',
        text: 'Rejected',
        status: 'REJECTED',
      }
    default:
      return {
        variant: 'secondary' as const,
        className: 'bg-gray-100 text-gray-800',
        icon: 'Circle',
        text: status,
        status: status,
      }
  }
}

export const getSwapProgressValue = (status: SwapStatus): number => {
  switch (status) {
    case SwapStatus.Requested:
      return 0
    case SwapStatus.Approved:
    case SwapStatus.Rejected:
      return 20
    case SwapStatus.TradeExecuting:
    case SwapStatus.TradeFailed:
      return 40
    case SwapStatus.TradeCompleted:
    case SwapStatus.WithdrawalInitiated:
    case SwapStatus.WithdrawalFailed:
      return 60
    case SwapStatus.WithdrawalCompleted:
    case SwapStatus.PushOutInitiated:
    case SwapStatus.PushOutFailed:
      return 80
    case SwapStatus.PushOutCompleted:
    case SwapStatus.Completed:
      return 100
    case SwapStatus.Cancelled:
      return 0
    default:
      return 0
  }
}

export const getSwapSteps = (): string[] => [
  'Created',
  'Initiated',
  'Executed Swap',
  'Transfer Main',
  'Transfer Client',
  'Completed',
]

export const getSwapStepState = (stepIndex: number, status: SwapStatus): StepState => {
  if (stepIndex === 0) {
    // Created
    if (status === SwapStatus.Requested) return StepState.Loading
    return StepState.Completed
  } else if (stepIndex === 1) {
    // Initiated
    if (status === SwapStatus.Rejected) return StepState.Failed
    if (status === SwapStatus.Approved) return StepState.Loading
    if (getSwapProgressValue(status) > 20) return StepState.Completed
    return StepState.Pending
  } else if (stepIndex === 2) {
    // Executed Swap
    if (status === SwapStatus.TradeFailed) return StepState.Failed
    if (status === SwapStatus.TradeExecuting) return StepState.Loading
    if (status === SwapStatus.TradeCompleted || getSwapProgressValue(status) > 40)
      return StepState.Completed
    return StepState.Pending
  } else if (stepIndex === 3) {
    // Transfer Main
    if (status === SwapStatus.WithdrawalFailed) return StepState.Failed
    if (status === SwapStatus.WithdrawalInitiated || status === SwapStatus.TradeCompleted)
      return StepState.Loading
    if (status === SwapStatus.WithdrawalCompleted || getSwapProgressValue(status) > 60)
      return StepState.Completed
    return StepState.Pending
  } else if (stepIndex === 4) {
    // Transfer Client
    if (status === SwapStatus.PushOutFailed) return StepState.Failed
    if (status === SwapStatus.PushOutInitiated || status === SwapStatus.WithdrawalCompleted)
      return StepState.Loading
    if (status === SwapStatus.PushOutCompleted || getSwapProgressValue(status) > 80)
      return StepState.Completed
    return StepState.Pending
  } else {
    // Completed
    if (status === SwapStatus.PushOutCompleted) return StepState.Loading
    if (status === SwapStatus.Completed) return StepState.Completed
    return StepState.Pending
  }
}

export const canApproveTransaction = (
  status: SwapStatus | DepositStatus,
  isComplianceUser: boolean,
  transactionType: 'TRADE' | 'DEPOSIT',
  isFlagged = false,
): boolean => {
  if (isComplianceUser && transactionType !== 'DEPOSIT') {
    return false
  }

  if (transactionType === 'DEPOSIT') {
    if (status !== DepositStatus.Initiated) {
      return false
    }
    if (isComplianceUser) {
      return true
    }
    return !isFlagged
  }

  return status === SwapStatus.Requested
}
